import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaUpload,
  FaTrash,
  FaDownload,
  FaFile,
  FaFilePdf,
  FaFileExcel,
  FaFileImage,
  FaFileCsv,
  FaSpinner,
  FaTimes,
  FaCheck
} from 'react-icons/fa';
import { useLanguage } from '../../i18n';
import useActivityTracker from '../../hooks/useActivityTracker';
import './FileManager.css';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

// Componente para icono de archivo segun tipo
const FileIcon = ({ type }) => {
  switch (type) {
    case 'pdf':
      return <FaFilePdf className="file-icon pdf" />;
    case 'excel':
      return <FaFileExcel className="file-icon excel" />;
    case 'csv':
      return <FaFileCsv className="file-icon csv" />;
    case 'image':
      return <FaFileImage className="file-icon image" />;
    default:
      return <FaFile className="file-icon" />;
  }
};

const FileManager = ({ username }) => {
  const { t } = useLanguage();
  const { trackAction } = useActivityTracker(username, 'files');
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const fileInputRef = useRef(null);

  // Cargar archivos
  const fetchFiles = useCallback(async () => {
    if (!username) return;

    try {
      setIsLoading(true);
      const response = await fetch(
        `${STRANDS_API_URL}/api/files?username=${encodeURIComponent(username)}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFiles(data.files || []);
        }
      } else if (response.status === 403) {
        setError(t('admin.noPermission'));
      }
    } catch (err) {
      console.error('[FileManager] Error fetching files:', err);
      setError(t('admin.errorLoadingFiles'));
    } finally {
      setIsLoading(false);
    }
  }, [username]);

  // Cargar estadisticas
  const fetchStats = useCallback(async () => {
    if (!username) return;

    try {
      const response = await fetch(
        `${STRANDS_API_URL}/api/files/stats?username=${encodeURIComponent(username)}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data.stats);
        }
      }
    } catch (err) {
      console.error('[FileManager] Error fetching stats:', err);
    }
  }, [username]);

  useEffect(() => {
    fetchFiles();
    fetchStats();
  }, [fetchFiles, fetchStats]);

  // Subir archivo
  const handleUpload = async (file) => {
    if (!file || !username) return;

    // Validar tipo
    const allowedExtensions = ['.csv', '.pdf', '.xlsx', '.xls', '.png', '.jpg', '.jpeg', '.gif'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allowedExtensions.includes(ext)) {
      setError(t('admin.fileTypeNotAllowed'));
      return;
    }

    // Validar tamano individual del archivo (500MB max por archivo)
    if (file.size > 500 * 1024 * 1024) {
      setError(t('admin.fileSizeExceeded'));
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadingFileName(file.name);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('username', username);

      // Simular progreso (fetch no soporta progreso nativo)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(`${STRANDS_API_URL}/api/files/upload`, {
        method: 'POST',
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          trackAction('upload_file', {
            file_id: data.file?.id,
            file_name: data.file?.originalName,
            file_type: data.file?.fileType,
            file_size_bytes: data.file?.fileSize
          });
          setSuccessMessage(`${t('admin.fileUploadedSuccess')}: "${data.file.originalName}"`);
          fetchFiles();
          fetchStats();

          // Limpiar mensaje despues de 3 segundos
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          trackAction('upload_file_failed', { error: data.error });
          setError(data.error || t('admin.errorUploadingFile'));
        }
      } else {
        const errorData = await response.json();
        trackAction('upload_file_failed', { error: errorData.error, status: response.status });
        setError(errorData.error || t('admin.errorUploadingFile'));
      }
    } catch (err) {
      console.error('[FileManager] Upload error:', err);
      setError(t('admin.connectionError'));
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Descargar archivo
  const handleDownload = async (fileId, fileName) => {
    try {
      const response = await fetch(
        `${STRANDS_API_URL}/api/files/${fileId}/download?username=${encodeURIComponent(username)}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.downloadUrl) {
          trackAction('download_file', { file_id: fileId, file_name: fileName });
          // Abrir URL de descarga en nueva pestana
          window.open(data.downloadUrl, '_blank');
        }
      } else {
        setError(t('admin.errorDownloading'));
      }
    } catch (err) {
      console.error('[FileManager] Download error:', err);
      setError(t('admin.errorDownloading'));
    }
  };

  // Eliminar archivo
  const handleDelete = async (fileId) => {
    try {
      const response = await fetch(
        `${STRANDS_API_URL}/api/files/${fileId}?username=${encodeURIComponent(username)}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          trackAction('delete_file', { file_id: fileId });
          setSuccessMessage(t('admin.fileDeletedSuccess'));
          fetchFiles();
          fetchStats();
          setTimeout(() => setSuccessMessage(null), 3000);
        }
      } else {
        setError(t('admin.errorDeleting'));
      }
    } catch (err) {
      console.error('[FileManager] Delete error:', err);
      setError(t('admin.errorDeleting'));
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Handlers de Drag & Drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleUpload(droppedFile);
    }
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleUpload(selectedFile);
    }
    // Reset input
    e.target.value = '';
  };

  return (
    <div className="file-manager">
      <div className="file-manager-header">
        <h3>{t('admin.fileManagerTitle')}</h3>
        {stats && (
          <div className="file-stats">
            <span className="stat-item">
              <strong>{stats.total_files || 0}</strong> {t('admin.filesCount')}
            </span>
            <span className="stat-item">
              <strong>{stats.total_size_formatted || '0 B'}</strong> {t('admin.total')}
            </span>
          </div>
        )}
      </div>

      {/* Mensajes de estado */}
      {error && (
        <div className="file-message error">
          <FaTimes className="message-icon" />
          <span>{error}</span>
          <button onClick={() => setError(null)}><FaTimes /></button>
        </div>
      )}

      {successMessage && (
        <div className="file-message success">
          <FaCheck className="message-icon" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Zona de subida */}
      <div
        className={`upload-zone ${isDragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          accept=".csv,.pdf,.xlsx,.xls,.png,.jpg,.jpeg,.gif"
          style={{ display: 'none' }}
        />

        {isUploading ? (
          <div className="upload-progress">
            <FaSpinner className="spinner" />
            <span>{t('admin.uploading')} "{uploadingFileName}"</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>{uploadProgress}%</span>
          </div>
        ) : (
          <>
            <FaUpload className="upload-icon" />
            <p>{t('admin.dragDropHint')}</p>
            <span className="upload-hint">{t('admin.fileTypesHint')}</span>
          </>
        )}
      </div>

      {/* Lista de archivos */}
      <div className="files-list">
        {isLoading ? (
          <div className="files-loading">
            <FaSpinner className="spinner" />
            <span>{t('admin.loadingFiles')}</span>
          </div>
        ) : files.length === 0 ? (
          <div className="no-files">
            <p>{t('admin.noFilesUploaded')}</p>
            <span>{t('admin.uploadFirstFile')}</span>
          </div>
        ) : (
          <table className="files-table">
            <thead>
              <tr>
                <th>{t('admin.file')}</th>
                <th>{t('admin.type')}</th>
                <th>{t('admin.size')}</th>
                <th>{t('admin.uploaded')}</th>
                <th>{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.id}>
                  <td className="file-name-cell">
                    <FileIcon type={file.file_type} />
                    <span title={file.original_name}>
                      {file.original_name.length > 30
                        ? file.original_name.substring(0, 30) + '...'
                        : file.original_name}
                    </span>
                  </td>
                  <td className="file-type-cell">
                    <span className={`file-type-badge ${file.file_type}`}>
                      {file.file_type.toUpperCase()}
                    </span>
                  </td>
                  <td>{file.file_size_formatted}</td>
                  <td>{file.uploaded_at_formatted}</td>
                  <td className="actions-cell">
                    <button
                      className="action-btn download"
                      onClick={() => handleDownload(file.id, file.original_name)}
                      title={t('admin.download')}
                    >
                      <FaDownload />
                    </button>
                    {file.original_name !== 'upc_tracking.csv' && (
                    <button
                      className="action-btn delete"
                      onClick={() => setDeleteConfirm(file)}
                      title={t('admin.delete')}
                    >
                      <FaTrash />
                    </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de confirmacion de eliminacion */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h4>{t('admin.confirmDelete')}</h4>
            <p>{t('admin.confirmDeleteMessage')} "{deleteConfirm.original_name}"?</p>
            <div className="modal-buttons">
              <button
                className="btn-confirm"
                onClick={() => handleDelete(deleteConfirm.id)}
              >
                {t('admin.delete')}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setDeleteConfirm(null)}
              >
                {t('admin.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileManager;
